// Public metadata only. The catalogue is registered by the docs entry, never
// imported into the shared footer or the general website prerender entry.
const guides = new Set(), operations = new Set();
const EVENTS = new Set(['developer_docs_viewed', 'developer_guide_viewed', 'developer_endpoint_viewed', 'developer_example_copied', 'developer_plan_filter_changed', 'developer_api_key_cta_clicked', 'developer_openapi_downloaded', 'developer_resources_clicked']);
export const isDeveloperEvent = (event) => EVENTS.has(event);
export function registerDocsCatalogue(bundle) {
  guides.clear(); operations.clear();
  bundle.guides.forEach(({ id }) => guides.add(id));
  bundle.navigation.forEach(({ id }) => operations.add(id));
}
export function docsEventProperties(event, input = {}) {
  if (!EVENTS.has(event)) return null;
  const properties = { documentationVersion: 'v1' };
  if (guides.has(input.guideId)) properties.guideId = input.guideId;
  if (operations.has(input.operationId)) properties.operationId = input.operationId;
  if (['curl', 'javascript', 'python', 'json'].includes(input.language)) properties.language = input.language;
  if (['all', 'free', 'entry', 'growth'].includes(input.plan)) properties.plan = input.plan;
  if (typeof input.personalized === 'boolean') properties.personalized = input.personalized;
  if (event === 'developer_resources_clicked') properties.location = 'footer';
  if (event === 'developer_guide_viewed' && !properties.guideId) return null;
  if (event === 'developer_endpoint_viewed' && !properties.operationId) return null;
  if (event === 'developer_example_copied' && (!properties.language || (!properties.guideId && !properties.operationId))) return null;
  return properties;
}

// This is the final transport boundary, after the SDK adds automatic fields.
// Person profiles, raw initial referrers, arbitrary callbacks and DOM data are
// deliberately absent. SDK referrer/campaign persistence is disabled for docs.
export function sanitizeDocsEvent(event, origin) {
  if (!event || (!EVENTS.has(event.event) && !['$pageview', '$pageleave', 'consent_choice'].includes(event.event))) return null;
  const source = event.properties || {};
  const properties = EVENTS.has(event.event) ? docsEventProperties(event.event, source) : {};
  if (!properties) return null;
  if (event.event === 'consent_choice') {
    properties.analytics = source.analytics === true;
    properties.marketing = source.marketing === true;
  }
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  for (const name of ['distinct_id', '$device_id', '$session_id', '$window_id']) if (typeof source[name] === 'string' && uuid.test(source[name])) properties[name] = source[name];
  // An existing marketing login can have an email distinct ID. Documentation
  // stays anonymous, using only the SDK's anonymous device UUID in that case.
  if (properties.$device_id) properties.distinct_id = properties.$device_id;
  if (typeof source.token === 'string') properties.token = source.token;
  for (const name of ['$lib', '$lib_version']) if (typeof source[name] === 'string' && /^[a-z0-9. -]{1,40}$/i.test(source[name])) properties[name] = source[name];
  properties.$is_identified = false;
  properties.$process_person_profile = false;
  const safePath = /^\/docs(?:\/(?:getting-started|authentication|use-cases|organizations-workspaces|api-reference|plans-limits|changelog|guides\/(?:clients-projects|imports|commercial-inputs|assumptions|investigations|scenarios|reports)))?\/?$/;
  try {
    const url = new URL(source.$current_url || origin, origin);
    const pathname = url.origin === origin && safePath.test(url.pathname) ? url.pathname.replace(/\/$/, '') : '/';
    properties.$current_url = origin + pathname;
    properties.$pathname = pathname;
    properties.$host = new URL(origin).hostname;
    // Only fixed public campaign categories survive. Free-text campaign IDs,
    // queries, hashes and private app referrers never reach transport.
    for (const [name, allowed] of Object.entries({ utm_source: ['google', 'linkedin', 'github', 'newsletter'], utm_medium: ['organic', 'cpc', 'email', 'social'] })) {
      const value = url.searchParams.get(name);
      if (allowed.includes(value)) properties[name] = value;
    }
    const referrer = new URL(source.$referrer || origin, origin);
    if (['www.google.com', 'google.com', 'www.linkedin.com', 'linkedin.com', 'github.com'].includes(referrer.hostname)) {
      properties.$referrer = referrer.origin + '/'; properties.$referring_domain = referrer.hostname;
    }
  } catch { return null; }
  return { event: event.event, properties, ...(event.uuid ? { uuid: event.uuid } : {}), ...(event.timestamp ? { timestamp: event.timestamp } : {}) };
}
